import React, { useEffect, useRef, useState } from 'react';
import { useHomepageSettings } from '../../hooks/useHomepageSettings';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const WhyChooseUs: React.FC = () => {
  const { data: settings, isLoading } = useHomepageSettings();

  const defaultHeading = 'Why Choose OnArrival Global';
  const defaultFeatures: Feature[] = [
    {
      icon: '🛡️',
      title: 'Peace of Mind',
      description:
        'Rest easy knowing your vacation home is monitored by local professionals who care as much about your property as you do.',
    },
    {
      icon: '🏡',
      title: 'Local Expertise',
      description:
        '10+ years serving Destin & 30A. We know the area, the weather patterns, and the best local contractors.',
    },
    {
      icon: '📱',
      title: 'Real-Time Updates',
      description:
        'Detailed inspection reports with photos sent directly to you after every visit. Always stay informed.',
    },
    {
      icon: '⚡',
      title: 'Rapid Response',
      description:
        "24/7 emergency response for urgent issues. We act as your eyes, ears, and hands when you can't be there.",
    },
  ];

  const displayHeading = settings?.whyChooseUs?.heading || defaultHeading;

  // Map Sanity features to component format
  const sanityFeatures: Feature[] =
    settings?.whyChooseUs?.features?.map((item) => ({
      icon: item.icon,
      title: item.title,
      description: item.description,
    })) || [];

  const displayFeatures =
    sanityFeatures.length > 0 ? sanityFeatures : defaultFeatures;
  const isEnabled = settings?.whyChooseUs?.enabled !== false;

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // Don't render if disabled
  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <section className="py-20 md:py-28 bg-gradient-to-b from-stone-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28 bg-gradient-to-b from-stone-50 to-white"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-primary-500 mb-4">
            {displayHeading}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 mx-auto rounded-full" />
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayFeatures.map((feature, index) => (
            <div
              key={index}
              className={`group transition-all duration-700 delay-${index * 100} ${
                isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Glass Morphism Card */}
              <div className="relative h-full p-8 rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group-hover:bg-white/80">
                {/* Gradient Border Effect on Hover */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-400/0 to-blue-400/0 group-hover:from-primary-400/20 group-hover:to-blue-400/20 transition-all duration-500 -z-10" />

                {/* Icon */}
                <div className="text-5xl mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                  {feature.icon}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-primary-500 mb-3 group-hover:text-primary-700 transition-colors">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-sand-700 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
